import { useEffect, useState } from 'react'
import {
  useDeadlineAlertSettings,
  useRunDeadlineAlerts,
  useSaveDeadlineAlertSettings
} from '../../api/queries'
import { Card, ErrorBox, Loading } from '../../components/ui'
import type { DeadlineAlertSettings } from '../../api/types'

/** De klokkeslæt der tilbydes. Hele timer er rigeligt til et morgenvarsel. */
const hours = Array.from({ length: 24 }, (_, hour) => hour)

/**
 * Opsætning af de automatiske deadline-varsler (kravspec afsnit 29).
 *
 * Varslerne sendes én gang i døgnet på det valgte klokkeslæt - ikke løbende.
 * Se docs/ARCHITECTURE.md for hvem der får hvilke varsler.
 */
export function DeadlineAlertsPage() {
  const settings = useDeadlineAlertSettings()
  const save = useSaveDeadlineAlertSettings()
  const run = useRunDeadlineAlerts()

  const [form, setForm] = useState<DeadlineAlertSettings | null>(null)

  useEffect(() => {
    if (settings.data) setForm(settings.data)
  }, [settings.data])

  if (settings.isLoading || !form) return <Loading />
  if (settings.error) return <ErrorBox error={settings.error} />

  const update = <K extends keyof DeadlineAlertSettings>(field: K, value: DeadlineAlertSettings[K]) =>
    setForm({ ...form, [field]: value })

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Deadline-varsler">
        <p className="small muted" style={{ marginTop: 0 }}>
          Systemet sender selv besked, når en deadline nærmer sig eller er overskredet. Der varsles
          på tre ting: sagens deadline (til projektlederen), opgavens deadline (til ansvarlig og
          medansvarlige) og sagens næste handling (til den, der er sat på).
        </p>

        <div className="stack" style={{ gap: 14 }}>
          <label className="row" style={{ gap: 8 }}>
            {/* width: auto - ellers fylder feltet hele bredden og skubber teksten væk. */}
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={form.enabled}
              onChange={(e) => update('enabled', e.target.checked)}
            />
            <span>Send deadline-varsler automatisk</span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Varsl så mange dage før deadline</span>
            <input
              type="number"
              min={1}
              max={30}
              value={form.warningDays}
              onChange={(e) => update('warningDays', Number(e.target.value))}
              style={{ maxWidth: 120 }}
            />
            <span className="tiny muted">
              Med {form.warningDays}{' '}
              {form.warningDays === 1 ? 'dag' : 'dage'} får man også besked om noget, der falder
              præcis om {form.warningDays} {form.warningDays === 1 ? 'dag' : 'dage'}.
            </span>
          </label>

          {/* Kalibreringsvinduet står her, fordi det er samme slags spørgsmål:
              hvor lang tid i forvejen vil man vide det? Det er bare længere -
              en kalibrering skal bestilles og instrumentet undværes imens. */}
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Varsl så mange dage før en kalibrering udløber</span>
            <input
              type="number"
              min={1}
              max={180}
              value={form.calibrationWarningDays}
              onChange={(e) => update('calibrationWarningDays', Number(e.target.value))}
              style={{ maxWidth: 120 }}
            />
            <span className="tiny muted">
              Gælder måleinstrumenterne. Varslet går til den, instrumentet er udleveret til,
              og til administratorerne. Er modulet Måleinstrumenter slået fra, sendes der ingenting.
            </span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Send dagens varsler kl.</span>
            <select
              value={form.runHour}
              onChange={(e) => update('runHour', Number(e.target.value))}
              style={{ maxWidth: 120 }}
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {String(hour).padStart(2, '0')}:00
                </option>
              ))}
            </select>
            <span className="tiny muted">
              Dansk tid. Har serveren været nede på klokkeslættet, sendes varslerne ved første
              lejlighed bagefter - og aldrig to gange for det samme.
            </span>
          </label>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 16 }}>
          <button
            className="btn btn--primary"
            disabled={save.isPending}
            onClick={() => save.mutate(form)}
          >
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Sender ...' : 'Send nu'}
          </button>
        </div>

        {save.isSuccess && !save.isPending && (
          <div className="small" style={{ marginTop: 10, color: 'var(--success)' }}>
            Opsætningen er gemt. Ændringen virker med det samme.
          </div>
        )}
        {save.error && <ErrorBox error={save.error} />}

        {run.data && (
          <div className="small" style={{ marginTop: 10 }}>
            {run.data.total === 0 ? (
              <>Der var ingen nye varsler at sende.</>
            ) : (
              <>
                Sendte {run.data.total} varsler til {run.data.recipients}{' '}
                {run.data.recipients === 1 ? 'bruger' : 'brugere'} - {run.data.soon} om kommende og{' '}
                {run.data.overdue} om overskredne deadlines.
              </>
            )}
          </div>
        )}
        {run.error && <ErrorBox error={run.error} />}
      </Card>

      <Card title="Sådan virker varslerne">
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
          <li>Der varsles på datoer i dansk tid, ikke på klokkeslæt.</li>
          <li>Færdige og annullerede opgaver varsles ikke, og heller ikke arkiverede sager.</li>
          <li>Den samme besked sendes kun én gang. Flyttes deadline, varsles der igen.</li>
          <li>
            En overskredet deadline varsles, uanset hvor gammel den er - ellers ville en glemt
            opgave falde ud af billedet.
          </li>
          <li>Varslerne lander under Notifikationer. E-mail og Teams kræver Microsoft 365.</li>
        </ul>
      </Card>
    </div>
  )
}
